import type { CityConfig } from "../city-template/types";

export const auburnMeConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — Maine CDC Vital Records", href: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/", description: "Official Maine vital records office in Augusta" },
    { label: "Marriage Licenses — Androscoggin County Clerk", href: "https://www.androscoggincountymaine.gov/", description: "Androscoggin County Clerk — marriage license issuance" },
    { label: "Divorce Records — Androscoggin County Clerk", href: "https://www.androscoggincountymaine.gov/", description: "Androscoggin County Clerk — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Maine CDC for online vital record orders" },
  ],
  city: "Auburn",
  state: "Maine",
  stateAbbr: "ME",
  slug: "auburn-me",
  route: "/auburn-me",
  title: "Emergency Passport Auburn, ME — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Auburn, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Auburn, Lewiston, Lisbon, and Minot travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Auburn, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Auburn and Androscoggin County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Auburn%20Maine%20downtown%20with%20Androscoggin%20River%2C%20historic%20mill%20district%20and%20beautiful%20bridges%2C%20tree-lined%20streets%20with%20classic%20New%20England%20architecture%2C%20warm%20afternoon%20golden%20light%2C%20professional%20cityscape%20photography%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20scenic%20small%20city%20scene%20along%20the%20river&width=1800&height=700&seq=auburn-me-hero-001&orientation=landscape",
  heroImageAlt: "Auburn Maine downtown with Androscoggin River and historic mill district",
  geoPosition: "44.0978;-70.2312",
  geoPlacename: "Auburn, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 140 miles (2.5 hours) from Auburn, ME",
  },
  nearbyCitiesZips: [
    { name: "Downtown Auburn", zips: "04210" },
    { name: "Lewiston", zips: "04240" },
    { name: "Lisbon Falls", zips: "04252" },
    { name: "Sabattus", zips: "04280" },
    { name: "Minot", zips: "04258" },
    { name: "Poland", zips: "04274" },
    { name: "Greene", zips: "04236" },
    { name: "Turner", zips: "04282" },
    { name: "Mechanic Falls", zips: "04256" },
    { name: "Livermore Falls", zips: "04254" },
    { name: "Wales", zips: "04280" },
    { name: "New Gloucester", zips: "04260" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Auburn departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Portland", route: "/portland-me" },
    { name: "Emergency passport help in Bangor", route: "/bangor" },
    { name: "Urgent passport services in Lewiston", route: "/lewiston" },
    { name: "Same-day passport help in South Portland", route: "/south-portland" },
    { name: "Expedited passport services in Augusta", route: "/augusta-me" },
    { name: "Passport courier services in Brunswick", route: "/brunswick" },
    { name: "Rush passport help in Biddeford", route: "/biddeford" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Auburn, Maine remotely via secure courier. We do not have a walk-in office in Auburn — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Auburn travelers.",
  fpcOfficeAddress: "1 Boston Pl, Boston, MA 02108",
  fpcOfficeDistance: "Serving all of Androscoggin County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Auburn and the surrounding Twin Cities area. From downtown Auburn to the outskirts near Lewiston, from the Androscoggin River communities to the surrounding towns — we help Auburn travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Auburn neighborhoods and nearby cities including Lewiston, Lisbon, Sabattus, Minot, and Poland. If you are in Androscoggin County or nearby Kennebec County, we have you covered.",
  howItWorksIntro: "Most Auburn travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "With Auburn being a key hub for central Maine travelers, passport delays can be costly. Here are the most common mistakes we see.",
  testimonialsIntro: "Auburn travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Auburn? Here are the most common ones we hear from Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Auburn international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Thomas G.",
      location: "Auburn, ME",
      rating: 5,
      text: "My flight to Dublin was in 3 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. I shipped from the Auburn UPS Store and had my passport the next day.",
      service: "Emergency Passport",
    },
    {
      name: "Catherine W.",
      location: "Lewiston, ME",
      rating: 5,
      text: "I needed to renew my passport for a family trip to Nova Scotia. They reviewed my documents and I had my passport in 5 days. Great service for central Maine.",
      service: "Passport Renewal",
    },
    {
      name: "Steven B.",
      location: "Sabattus, ME",
      rating: 5,
      text: "Lost my passport a week before a business trip to Toronto. Fast Passport Center handled the DS-64 process and got me a replacement in 2 days. Truly a lifesaver.",
      service: "Lost Passport",
    },
    {
      name: "Nancy H.",
      location: "Poland, ME",
      rating: 5,
      text: "As a frequent traveler from the Auburn area, I need a reliable passport service. Fast Passport Center has been my go-to for years. Professional, fast, and always dependable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "auburn-1",
      name: "Auburn Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "49 New Auburn St",
      city: "Auburn",
      state: "ME",
      zip: "04210",
      phone: "(207) 782-2911",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Auburn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "auburn-2",
      name: "Lewiston Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "49 Ash St",
      city: "Lewiston",
      state: "ME",
      zip: "04240",
      phone: "(207) 784-1560",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1 mile from downtown Auburn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Lewiston-side residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "auburn-3",
      name: "Lisbon Falls Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25 Main St",
      city: "Lisbon Falls",
      state: "ME",
      zip: "04252",
      phone: "(207) 353-4421",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles from downtown Auburn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good option for Lisbon residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "auburn-4",
      name: "Sabattus Town Office",
      type: "Town Office Passport Acceptance",
      address: "190 Middle Rd",
      city: "Sabattus",
      state: "ME",
      zip: "04280",
      phone: "(207) 375-4811",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5 miles from downtown Auburn",
      website: "https://www.sabattus.org/",
      websiteLabel: "Sabattus Town Office",
      notes: "Accepts walk-ins but appointments preferred. Photo services not available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "auburn-5",
      name: "Minot Town Office",
      type: "Town Office Passport Acceptance",
      address: "293 Minot Center Rd",
      city: "Minot",
      state: "ME",
      zip: "04258",
      phone: "(207) 345-3301",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from downtown Auburn",
      website: "https://www.minotme.org/",
      websiteLabel: "Minot Town Office",
      notes: "Small town office with limited hours. Call ahead to schedule passport acceptance.",
      icon: "ri-government-line",
    },
    {
      id: "auburn-6",
      name: "Poland Town Office",
      type: "Town Office Passport Acceptance",
      address: "1231 Maine St",
      city: "Poland",
      state: "ME",
      zip: "04274",
      phone: "(207) 998-4661",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "12 miles from downtown Auburn",
      website: "https://www.polandtownoffice.org/",
      websiteLabel: "Poland Town Office",
      notes: "Popular for Poland and surrounding towns. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Auburn acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Auburn applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Auburn families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Auburn travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Auburn." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Auburn address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Auburn, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Auburn in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Auburn travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Auburn, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Auburn residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Auburn, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Auburn with no office visit required. We serve all Auburn neighborhoods and the entire Twin Cities area including Lewiston, Lisbon, Sabattus, and Minot. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Auburn, Maine?",
      a: "The fastest way to get an urgent passport in Auburn is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (3–5 weeks).",
    },
    {
      q: "How fast can I get a passport in Auburn, Maine?",
      a: "With Fast Passport Center's emergency courier service, Auburn travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Auburn?",
      a: "Popular options include the Auburn Post Office (04210), Lewiston Post Office (04240), Lisbon Falls Post Office (04252), and Sabattus Town Office (04280). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Lewiston, Lisbon, or Sabattus?",
      a: "We serve all of Androscoggin County including Lewiston, Lisbon, Sabattus, Minot, and Poland. Our overnight shipping labels work from any UPS location, so your location within the Auburn-Lewiston metro doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Auburn?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Auburn?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and UPS Store locations in Auburn and Lewiston offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Auburn?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Auburn?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Auburn families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Auburn", address: "449 Mount Auburn Ave, Auburn, ME 04210", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Auburn", address: "111 Turner St, Auburn, ME 04210", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "UPS Store — Auburn", address: "15 Minot Ave, Auburn, ME 04210", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Auburn", address: "449 Mount Auburn Ave, Auburn, ME 04210", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Rite Aid — Lewiston", address: "626 Lisbon St, Lewiston, ME 04240", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "Walgreens — Sabattus", address: "178 Middle Rd, Sabattus, ME 04280", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
    { name: "CVS Pharmacy — Poland", address: "1231 Maine St, Poland, ME 04274", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Turner", address: "73 General Turner Hill Rd, Turner, ME 04282", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
  ],
  dmvLocations: [
    {
      name: "BMV — Auburn",
      address: "321 Minot Ave, Auburn, ME 04210",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Lewiston",
      address: "36 Mollison Way, Lewiston, ME 04240",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "BMV — Lisbon Falls",
      address: "68 Main St, Lisbon Falls, ME 04252",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "BMV — Turner",
      address: "73 General Turner Hill Rd, Turner, ME 04282",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center. Online appointment system available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "40 miles from Auburn (45 minutes)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport with direct flights to Canada, Florida, and major U.S. hubs. International connections through Boston, NYC, and Philadelphia.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: true,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "75 miles from Auburn (1.5 hours)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport with seasonal international flights. Alternative for northern Maine travelers.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "140 miles from Auburn (2.5 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and Latin America. Many central Maine travelers connect through Boston for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
  ],
};